import * as Phaser from 'phaser';
import { calculateZoomScroll } from './zoomMath';

const wheelZoomSensitivity = 0.0015;
export type ScreenPoint = { readonly x: number; readonly y: number };

export class CameraControls {
  private userZoom = 1;
  private baseZoom = 1;

  constructor(
    private readonly camera: Phaser.Cameras.Scene2D.Camera,
    private readonly scale: Phaser.Scale.ScaleManager,
    private readonly boardWidth: number,
    private readonly boardHeight: number,
    /** 盤面の周囲に必ず残す余白（ワールド単位）。 */
    private readonly margin: number,
    /** 画面端に残す余白（ゲーム単位 = 物理ピクセル）。 */
    private readonly viewportMargin: number,
  ) {}

  reset(): void {
    this.userZoom = 1;
    this.baseZoom = 1;
  }

  configure(recenter: boolean): void {
    const availableWidth = Math.max(this.scale.width - this.viewportMargin * 2, 1);
    const availableHeight = Math.max(this.scale.height - this.viewportMargin * 2, 1);

    this.baseZoom = Math.min(
      availableWidth / (this.boardWidth + this.margin * 2),
      availableHeight / (this.boardHeight + this.margin * 2),
    );

    const horizontalMargin = Math.max(
      this.margin,
      (this.scale.width / this.baseZoom - this.boardWidth) / 2,
    );
    const verticalMargin = Math.max(
      this.margin,
      (this.scale.height / this.baseZoom - this.boardHeight) / 2,
    );
    this.camera.setBounds(
      -horizontalMargin,
      -verticalMargin,
      this.boardWidth + horizontalMargin * 2,
      this.boardHeight + verticalMargin * 2,
    );
    this.camera.setZoom(this.baseZoom * this.userZoom);
    if (recenter) {
      this.camera.centerOn(this.boardWidth / 2, this.boardHeight / 2);
    } else {
      this.camera.scrollX = this.camera.clampX(this.camera.scrollX);
      this.camera.scrollY = this.camera.clampY(this.camera.scrollY);
    }
  }

  zoomAround(screenX: number, screenY: number, nextUserZoom: number): void {
    this.userZoom = Phaser.Math.Clamp(nextUserZoom, 1, 2.35);
    const zoom = this.baseZoom * this.userZoom;
    const scroll = calculateZoomScroll({
      scrollX: this.camera.scrollX,
      scrollY: this.camera.scrollY,
      viewportWidth: this.camera.width,
      viewportHeight: this.camera.height,
      screenX,
      screenY,
      zoom: this.camera.zoom,
      nextZoom: zoom,
    });
    this.camera.setZoom(zoom);
    this.camera.scrollX = this.camera.clampX(scroll.scrollX);
    this.camera.scrollY = this.camera.clampY(scroll.scrollY);
  }

  applyPinch(factor: number, midpoint: ScreenPoint): void {
    this.zoomAround(midpoint.x, midpoint.y, this.userZoom * factor);
  }

  applyWheel(screenX: number, screenY: number, deltaY: number): void {
    const factor = Math.exp(-deltaY * wheelZoomSensitivity);
    this.zoomAround(screenX, screenY, this.userZoom * factor);
  }
}
